import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { apiRequest, downloadApiFile } from '../../../api/client';
import { useAuth } from '../../../auth/AuthContext';
import '../technician.css';

type TimeEntry = {
    _id: string;
    date: string;
    hours: number;
    siteName?: string;
    workOrderTitle?: string;
    kind?: 'manual' | 'clock';
    clockInAt?: string | null;
    clockOutAt?: string | null;
    notes?: string;
};

type InspectionRow = {
    _id: string;
    siteAddress?: string;
    status?: 'in_progress' | 'completed';
    standardCode?: string;
    frequency?: string;
    createdAt?: string;
    completedAt?: string;
    items?: Array<{ result?: string | null }>;
};

type WorkOrderExportRow = {
    _id: string;
    title?: string;
    status?: string;
    client?: { name?: string; address?: string };
    schedule?: { date?: string };
    formTemplateId?: string;
    createdAt?: string;
    reportSummary?: {
        reportCount: number;
        submittedCount: number;
        latestReportDate?: string | null;
        latestSubmittedAt?: string | null;
    };
};

type Range = 'this_week' | 'this_month' | 'last_30' | 'all';

function rangeStart(range: Range): number {
    const now = new Date();
    if (range === 'this_week') {
        const d = new Date(now);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1;
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - diff);
        return d.getTime();
    }
    if (range === 'this_month') {
        return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    }
    if (range === 'last_30') {
        return Date.now() - 30 * 86_400_000;
    }
    return 0;
}

function rangeLabel(range: Range): string {
    switch (range) {
        case 'this_week':
            return 'This week';
        case 'this_month':
            return 'This month';
        case 'last_30':
            return 'Last 30 days';
        default:
            return 'All time';
    }
}

function workOrderRangeDate(wo: WorkOrderExportRow): number {
    const schedule = wo.schedule?.date ? new Date(wo.schedule.date).getTime() : NaN;
    if (Number.isFinite(schedule)) return schedule;
    return new Date(wo.createdAt || 0).getTime();
}

function escapeCsv(value: unknown): string {
    const str = value === null || value === undefined ? '' : String(value);
    if (/[",\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}

function rowsToCsv(headers: string[], rows: Array<Array<unknown>>): string {
    const lines = [headers.map(escapeCsv).join(',')];
    for (const r of rows) lines.push(r.map(escapeCsv).join(','));
    return lines.join('\n');
}

function triggerCsvDownload(filename: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
}

function workOrderFormLabel(wo: WorkOrderExportRow): string {
    if (!wo.formTemplateId) return 'No form';
    const summary = wo.reportSummary;
    if (!summary || summary.submittedCount === 0) return 'Not submitted';
    if (summary.submittedCount > 0) return 'Submitted';
    return 'Draft';
}

export default function Report(): React.JSX.Element {
    const { user } = useAuth();
    const [range, setRange] = useState<Range>('this_week');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [entries, setEntries] = useState<TimeEntry[]>([]);
    const [inspections, setInspections] = useState<InspectionRow[]>([]);
    const [workOrders, setWorkOrders] = useState<WorkOrderExportRow[]>([]);
    const [downloading, setDownloading] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [t, i, w] = await Promise.all([
                apiRequest<{ data: TimeEntry[] }>('/api/time-entries'),
                apiRequest<{ data: InspectionRow[] }>('/api/inspections'),
                apiRequest<{ data: WorkOrderExportRow[] }>('/api/work-orders?allAssigned=1')
            ]);
            setEntries(t.data || []);
            setInspections(i.data || []);
            setWorkOrders(w.data || []);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load report data.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const filtered = useMemo(() => {
        const since = rangeStart(range);
        return {
            entries: entries.filter((e) => new Date(e.date || 0).getTime() >= since),
            inspections: inspections.filter(
                (i) => new Date(i.completedAt || i.createdAt || 0).getTime() >= since
            ),
            workOrders: workOrders.filter((wo) => workOrderRangeDate(wo) >= since)
        };
    }, [range, entries, inspections, workOrders]);

    const stats = useMemo(() => {
        const totalHours = filtered.entries.reduce(
            (sum, e) => sum + (Number(e.hours) || 0),
            0
        );
        const completed = filtered.inspections.filter((i) => i.status === 'completed').length;
        const inProgress = filtered.inspections.filter((i) => i.status === 'in_progress').length;
        const failed = filtered.inspections.reduce((sum, i) => {
            const items = Array.isArray(i.items) ? i.items : [];
            return (
                sum +
                items.filter((it) => it.result === 'fail' || it.result === 'deficient').length
            );
        }, 0);
        const withForms = filtered.workOrders.filter((wo) => Boolean(wo.formTemplateId)).length;
        const submittedForms = filtered.workOrders.filter(
            (wo) => (wo.reportSummary?.submittedCount || 0) > 0
        ).length;
        return {
            totalHours,
            completed,
            inProgress,
            workOrders: filtered.workOrders.length,
            withForms,
            submittedForms,
            failed
        };
    }, [filtered]);

    function exportTimeCsv() {
        const rows = filtered.entries.map((e) => [
            e.date ? new Date(e.date).toISOString().slice(0, 10) : '',
            e.kind || 'manual',
            e.clockInAt ? new Date(e.clockInAt).toLocaleTimeString() : '',
            e.clockOutAt ? new Date(e.clockOutAt).toLocaleTimeString() : '',
            Number(e.hours || 0).toFixed(2),
            e.siteName || '',
            e.workOrderTitle || '',
            e.notes || ''
        ]);
        triggerCsvDownload(
            `timesheet-${range}-${new Date().toISOString().slice(0, 10)}.csv`,
            rowsToCsv(
                ['Date', 'Kind', 'Clock in', 'Clock out', 'Hours', 'Site', 'Work order', 'Notes'],
                rows
            )
        );
    }

    function exportInspectionsCsv() {
        const rows = filtered.inspections.map((i) => {
            const items = Array.isArray(i.items) ? i.items : [];
            const pass = items.filter((it) => it.result === 'pass').length;
            const fail = items.filter((it) => it.result === 'fail' || it.result === 'deficient').length;
            const na = items.filter((it) => it.result === 'n/a').length;
            return [
                i._id,
                i.siteAddress || '',
                i.frequency || '',
                i.standardCode || '',
                i.status || '',
                i.createdAt ? new Date(i.createdAt).toISOString().slice(0, 10) : '',
                i.completedAt ? new Date(i.completedAt).toISOString().slice(0, 10) : '',
                items.length,
                pass,
                fail,
                na
            ];
        });
        triggerCsvDownload(
            `inspections-${range}-${new Date().toISOString().slice(0, 10)}.csv`,
            rowsToCsv(
                [
                    'Inspection id',
                    'Site',
                    'Frequency',
                    'Standard',
                    'Status',
                    'Created',
                    'Completed',
                    'Total items',
                    'Pass',
                    'Fail',
                    'N/A'
                ],
                rows
            )
        );
    }

    function exportWorkOrdersCsv() {
        const rows = filtered.workOrders.map((wo) => [
            wo._id,
            wo.title || '',
            wo.client?.name || '',
            wo.client?.address || '',
            wo.status || '',
            wo.schedule?.date ? new Date(wo.schedule.date).toISOString().slice(0, 10) : '',
            wo.formTemplateId ? 'yes' : 'no',
            workOrderFormLabel(wo),
            wo.reportSummary?.reportCount || 0,
            wo.reportSummary?.submittedCount || 0,
            wo.reportSummary?.latestSubmittedAt
                ? new Date(wo.reportSummary.latestSubmittedAt).toISOString().slice(0, 10)
                : ''
        ]);
        triggerCsvDownload(
            `work-orders-${range}-${new Date().toISOString().slice(0, 10)}.csv`,
            rowsToCsv(
                [
                    'Work order id',
                    'Title',
                    'Client',
                    'Address',
                    'Status',
                    'Scheduled',
                    'Has form',
                    'Form status',
                    'Report count',
                    'Submitted count',
                    'Last submitted'
                ],
                rows
            )
        );
    }

    async function downloadInspectionPdf(id: string) {
        if (downloading) return;
        setDownloading(id);
        try {
            await downloadApiFile(`/api/inspections/${id}/pdf`, `Inspection-${id}.pdf`);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to download PDF.');
        } finally {
            setDownloading(null);
        }
    }

    return (
        <AppShell>
            <div className="tech-page">
                <header className="tech-hero">
                    <div>
                        <p className="tech-kicker">Reports</p>
                        <h1 className="tech-title">Export Report</h1>
                        <p className="tech-sub">
                            Download CSV summaries of your work, assigned job forms, and inspection PDFs.
                            {user?.name ? ` Signed in as ${user.name}.` : ''}
                        </p>
                    </div>
                    <div className="tech-actions">
                        <Link to="/technician/TimeSheet" className="tech-btn tech-btn-ghost">
                            Time sheet
                        </Link>
                        <Link to="/technician/JobList" className="tech-btn tech-btn-ghost">
                            Job list
                        </Link>
                    </div>
                </header>

                {error ? (
                    <div className="tech-banner tech-banner-error" role="alert">
                        <span>{error}</span>
                        <button
                            type="button"
                            className="tech-banner-close"
                            onClick={() => setError(null)}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    </div>
                ) : null}

                <div role="tablist" aria-label="Range" className="tech-tabbar" style={{ maxWidth: 520 }}>
                    {(['this_week', 'this_month', 'last_30', 'all'] as const).map((r) => (
                        <button
                            key={r}
                            type="button"
                            role="tab"
                            aria-selected={range === r}
                            className={`tech-tabbar__tab${range === r ? ' is-active' : ''}`}
                            onClick={() => setRange(r)}
                        >
                            {rangeLabel(r)}
                        </button>
                    ))}
                </div>

                <section className="tech-report-grid">
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Hours logged</span>
                        <span className="tech-report-stat-value">{stats.totalHours.toFixed(1)}h</span>
                        <span className="tech-report-stat-hint">
                            Across {filtered.entries.length} entr{filtered.entries.length === 1 ? 'y' : 'ies'}.
                        </span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Completed inspections</span>
                        <span className="tech-report-stat-value">{stats.completed}</span>
                        <span className="tech-report-stat-hint">
                            {stats.inProgress > 0 ? `${stats.inProgress} still in progress.` : 'No open visits.'}
                        </span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Assigned jobs</span>
                        <span className="tech-report-stat-value">{stats.workOrders}</span>
                        <span className="tech-report-stat-hint">
                            {stats.withForms > 0
                                ? `${stats.submittedForms} of ${stats.withForms} forms submitted.`
                                : 'Includes scheduled and completed work orders.'}
                        </span>
                    </article>
                    <article className="tech-card tech-report-stat">
                        <span className="tech-report-stat-label">Lines failed</span>
                        <span className="tech-report-stat-value">{stats.failed}</span>
                        <span className="tech-report-stat-hint">Across completed checklists in this range.</span>
                    </article>
                </section>

                <section className="tech-card tech-report-section" style={{ marginBottom: 18 }}>
                    <div className="tech-report-section-head">
                        <h3>Time sheet</h3>
                        <div className="tech-report-section-actions">
                            <button
                                type="button"
                                className="tech-btn tech-btn-primary"
                                onClick={exportTimeCsv}
                                disabled={filtered.entries.length === 0}
                            >
                                Download CSV
                            </button>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {loading ? (
                            <p className="tech-report-empty">Loading…</p>
                        ) : filtered.entries.length === 0 ? (
                            <p className="tech-report-empty">No time entries in this range.</p>
                        ) : (
                            <ul className="tech-report-list">
                                {filtered.entries.slice(0, 8).map((e) => (
                                    <li key={e._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {formatDate(e.date)}
                                                {e.workOrderTitle ? ` · ${e.workOrderTitle}` : ''}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                {e.kind === 'clock' ? 'Clock entry' : 'Manual'} ·{' '}
                                                {e.siteName || '—'}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            <span className="tech-report-row-right-strong">
                                                {Number(e.hours || 0).toFixed(2)}h
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <section className="tech-card tech-report-section" style={{ marginBottom: 18 }}>
                    <div className="tech-report-section-head">
                        <h3>Inspections</h3>
                        <div className="tech-report-section-actions">
                            <button
                                type="button"
                                className="tech-btn tech-btn-primary"
                                onClick={exportInspectionsCsv}
                                disabled={filtered.inspections.length === 0}
                            >
                                Download CSV
                            </button>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {loading ? (
                            <p className="tech-report-empty">Loading…</p>
                        ) : filtered.inspections.length === 0 ? (
                            <p className="tech-report-empty">No inspections in this range.</p>
                        ) : (
                            <ul className="tech-report-list">
                                {filtered.inspections.slice(0, 8).map((i) => (
                                    <li key={i._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {i.siteAddress || `Inspection …${i._id.slice(-6)}`}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                {(i.frequency || 'annual')} ·{' '}
                                                {(i.standardCode || 'ULC_S536')} ·{' '}
                                                {i.status === 'completed' ? 'Completed' : 'In progress'}{' '}
                                                {(i.completedAt || i.createdAt) ? `· ${formatDate(i.completedAt || i.createdAt)}` : ''}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            {i.status === 'completed' ? (
                                                <button
                                                    type="button"
                                                    className="tech-btn tech-btn-primary"
                                                    onClick={() => void downloadInspectionPdf(i._id)}
                                                    disabled={downloading === i._id}
                                                    style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                                >
                                                    {downloading === i._id ? 'Downloading…' : 'PDF'}
                                                </button>
                                            ) : (
                                                <Link
                                                    to={`/technician/inspections/${i._id}/checklist`}
                                                    className="tech-btn tech-btn-ghost"
                                                    style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                                >
                                                    Resume
                                                </Link>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                <section className="tech-card tech-report-section">
                    <div className="tech-report-section-head">
                        <h3>Work orders</h3>
                        <div className="tech-report-section-actions">
                            <button
                                type="button"
                                className="tech-btn tech-btn-primary"
                                onClick={exportWorkOrdersCsv}
                                disabled={filtered.workOrders.length === 0}
                            >
                                Download CSV
                            </button>
                        </div>
                    </div>
                    <div className="tech-report-section-body">
                        {loading ? (
                            <p className="tech-report-empty">Loading…</p>
                        ) : filtered.workOrders.length === 0 ? (
                            <p className="tech-report-empty">No assigned work orders in this range.</p>
                        ) : (
                            <ul className="tech-report-list">
                                {filtered.workOrders.map((wo) => (
                                    <li key={wo._id} className="tech-report-list-row">
                                        <div className="tech-report-row-main">
                                            <div className="tech-report-row-title">
                                                {wo.title || `Job …${wo._id.slice(-6)}`}
                                            </div>
                                            <div className="tech-report-row-meta">
                                                {wo.client?.name || wo.client?.address || '—'}
                                                {wo.schedule?.date ? ` · ${formatDate(wo.schedule.date)}` : ''}
                                            </div>
                                        </div>
                                        <div className="tech-report-row-right">
                                            <span className="tech-badge ok">{(wo.status || 'scheduled').replace('-', ' ')}</span>
                                            <span className="tech-report-row-right-strong">
                                                {workOrderFormLabel(wo)}
                                            </span>
                                            {wo.formTemplateId ? (
                                                <Link
                                                    to="/technician/JobLog"
                                                    className="tech-btn tech-btn-ghost"
                                                    style={{ minHeight: 36, padding: '6px 14px', fontSize: 13 }}
                                                >
                                                    Job log
                                                </Link>
                                            ) : null}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>
        </AppShell>
    );
}
